import { z } from "zod";

interface Grid {
  pageW: number;
  pageH: number;
  cols: number;
  rows: number;
  labelW: number;
  labelH: number;
  gapX: number;
  gapY: number;
}

/**
 * Formaat van het QR-labelvel (alles in mm). Het raster van `cols × rows` labels staat gecentreerd op de pagina;
 * voor een labelprinter is de pagina zelf één label (1 × 1).
 */
export const labelLayoutSchema = z
  .object({
    /** Naam van de voorinstelling of "Eigen formaat"; alleen ter herkenning. */
    name: z.string().trim().max(60).default("Eigen formaat"),
    pageW: z.number().min(15).max(1000),
    pageH: z.number().min(10).max(1000),
    cols: z.number().int().min(1).max(20),
    rows: z.number().int().min(1).max(40),
    labelW: z.number().min(10).max(1000),
    labelH: z.number().min(8).max(1000),
    gapX: z.number().min(0).max(100).default(0),
    gapY: z.number().min(0).max(100).default(0),
    /** Lichte snijlijn rond elk label (handig op gewoon papier, niet op stickervellen). */
    cutLines: z.boolean().default(true),
  })
  .refine((l) => labelGridFits(l), { message: "De labels passen niet op de pagina" });
export type LabelLayout = z.output<typeof labelLayoutSchema>;
export type LabelLayoutBody = z.input<typeof labelLayoutSchema>;

/** Speling van 0,5 mm voor afrondingen in de maten van stickervellen. */
export const labelGridFits = (l: Grid): boolean =>
  l.cols * l.labelW + (l.cols - 1) * l.gapX <= l.pageW + 0.5 &&
  l.rows * l.labelH + (l.rows - 1) * l.gapY <= l.pageH + 0.5;

/** Grootst passende labelmaat bij een pagina, raster en tussenruimte, met `margin` mm rand rondom. */
export function fitLabelSize(l: Omit<Grid, "labelW" | "labelH">, margin = 0) {
  const round = (n: number) => Math.floor(n * 10) / 10;
  return {
    labelW: round((l.pageW - 2 * margin - (l.cols - 1) * l.gapX) / l.cols),
    labelH: round((l.pageH - 2 * margin - (l.rows - 1) * l.gapY) / l.rows),
  };
}

export const labelsPerPage = (l: Pick<LabelLayout, "cols" | "rows">) => l.cols * l.rows;

/** Zelfde maten (de naam telt niet mee): voor het ontdubbelen van de laatst gebruikte formaten. */
export const sameLabelLayout = (a: LabelLayout, b: LabelLayout) =>
  (["pageW", "pageH", "cols", "rows", "labelW", "labelH", "gapX", "gapY", "cutLines"] as const).every(
    (k) => a[k] === b[k],
  );

/** Zoveel laatst gebruikte formaten bewaart een organisatie. */
export const RECENT_LABEL_LAYOUTS = 5;

export const PAPER_SIZES = [
  { key: "A4", label: "A4 (210 × 297 mm)", w: 210, h: 297 },
  { key: "A5", label: "A5 (148 × 210 mm)", w: 148, h: 210 },
  { key: "A6", label: "A6 (105 × 148 mm)", w: 105, h: 148 },
  { key: "Letter", label: "Letter (216 × 279 mm)", w: 215.9, h: 279.4 },
] as const;

const sheet = (name: string, cols: number, rows: number, labelW: number, labelH: number, gapX = 0, cutLines = false) =>
  labelLayoutSchema.parse({ name, pageW: 210, pageH: 297, cols, rows, labelW, labelH, gapX, cutLines });
const roll = (name: string, w: number, h: number) =>
  labelLayoutSchema.parse({ name, pageW: w, pageH: h, cols: 1, rows: 1, labelW: w, labelH: h, cutLines: false });

/** Veelgebruikte stickervellen (A4) en labelprinterrollen. De eerste is het standaardformaat. */
export const LABEL_PRESETS: LabelLayout[] = [
  sheet("Stickervel 2 × 6 (90 × 42 mm)", 2, 6, 90, 42, 0, true),
  sheet("Stickervel 2 × 7 (99,1 × 38,1 mm, L7163)", 2, 7, 99.1, 38.1, 2.5),
  sheet("Stickervel 3 × 7 (63,5 × 38,1 mm, L7160)", 3, 7, 63.5, 38.1, 2.5),
  sheet("Stickervel 3 × 8 (70 × 37 mm)", 3, 8, 70, 37),
  sheet("Stickervel 4 × 10 (48,5 × 25,4 mm)", 4, 10, 48.5, 25.4),
  roll("Labelprinter 62 × 29 mm", 62, 29),
  roll("Labelprinter 54 × 25 mm", 54, 25),
  roll("Labelprinter 102 × 51 mm", 102, 51),
];
export const DEFAULT_LABEL_LAYOUT = LABEL_PRESETS[0]!;

export const labelLayoutsSchema = z.array(labelLayoutSchema).max(RECENT_LABEL_LAYOUTS);
export interface LabelLayoutsDto {
  /** Laatst gebruikte formaten van de organisatie, nieuwste eerst. */
  recent: LabelLayout[];
}
